import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPersonalMfaMethod from 'lumen/components/settings/personal/mfa-method';
import SettingsPersonalMfaModal from 'lumen/components/settings/personal/mfa-modal';
import type {
  MfaConfig,
  MfaMethodType,
} from 'lumen/utils/constants/mfa-constants';
import { MFA_MODAL } from 'lumen/utils/constants/mfa-constants';
import {
  MFA_METHODS_ICONS,
  MFA_METHODS_TRANSLATIONS,
  MFA_TYPES,
} from 'password-form/utils/constant-values';

interface SettingsPersonalMfaSignature {
  Args: {
    areConfigsLoading: boolean;
    mfaConfigs: MfaConfig[];
    refreshMfaConfigs: () => void;
  };
}

export default class SettingsPersonalMfa extends Component<SettingsPersonalMfaSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare localStorage: Services['local-storage'];

  @tracked isMfaModalOpen = false;
  @tracked showMfaTurnOffOrUpdateScreen = false;

  get isMfaEnabled(): boolean {
    return this.args.mfaConfigs.length > 0;
  }

  get isMfaEnforced(): boolean {
    return this.hotelAndMarketService.user.enforceMfa;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mfaActiveMethod() {
    if (!this.isMfaEnabled) {
      return {} as MfaMethodType;
    }

    // find the value inside of mfaActiveMethods array with the lowest createdAt value (i.e. the oldest method)
    const method = this.args.mfaConfigs.reduce((prev, curr) => {
      if (!prev || curr.enrolledAt < prev.enrolledAt) {
        return curr;
      }
      return prev;
    });

    const methodData: MfaMethodType = {
      id: method.gipDisplayName,
      icon: MFA_METHODS_ICONS[method.gipDisplayName],
      name: MFA_METHODS_TRANSLATIONS[method.gipDisplayName],
      info: '',
    };

    // TODO-MFA-EMAIL: currently email is filtered out from the available methods, as we are not supporting email as a MFA method right now
    /*
    if (methodType === MFA_TYPES.EMAIL) {
      methodData.info = this.hotelAndMarketService.user.email;
    } */

    if (method.gipDisplayName === MFA_TYPES.SMS) {
      methodData.info = method.meta.phoneNumber;
    }

    return methodData;
  }

  constructor(owner: Owner, args: SettingsPersonalMfaSignature['Args']) {
    super(owner, args);

    // check if the MFA modal was previously open, before the user had to login again
    if (this.localStorage.getItem(MFA_MODAL)) {
      this.isMfaModalOpen = true;
    }
  }

  @action
  changeMfaModalVisibility(): void {
    this.isMfaModalOpen = !this.isMfaModalOpen;
    this.localStorage.removeItem(MFA_MODAL);
  }

  @action
  onUpdateMfaClicked(): void {
    this.changeMfaModalVisibility();

    if (!this.isMfaEnforced) {
      this.showMfaTurnOffOrUpdateScreen = true;
    }
  }

  @action
  toggleMfaTurnOffOrUpdateScreen(): void {
    this.showMfaTurnOffOrUpdateScreen = !this.showMfaTurnOffOrUpdateScreen;
  }

  <template>
    <div>
      {{#if @areConfigsLoading}}
        <PrismLoadingBar />
      {{/if}}

      <PrismCard class="form-card">
        <:header as |Header|>
          <Header.title>
            {{t "general.mfa"}}
          </Header.title>
        </:header>
        <:content>

          {{#if this.isMfaEnforced}}
            <PrismBanner
              @bannerMessage={{t "mfa.banner_message"}}
              @bannerStyle="warning"
              @icon="warning"
              class="mb-400"
              data-test-id="banner-confirmation"
            />
          {{else}}
            <p class="text-300--regular mb-400">{{t "mfa.description"}}
              <a
                class="prism-link"
                href="https://help.mylighthouse.com/en/articles/4570883-guide-to-multi-factor-authentication-mfa"
                rel="noopener noreferrer"
                target="_blank"
              >
                {{t "general.learn_more"}}
                <PrismIcon @color="inherit" @name="link-out" @size="small" />
              </a>
            </p>
          {{/if}}

          {{#if @areConfigsLoading}}
            <div class="loading-pulse w-1/3"></div>
          {{else if this.isMfaEnabled}}
            <SettingsPersonalMfaMethod
              @mfaActiveMethod={{this.mfaActiveMethod}}
              @updateMfaClicked={{this.onUpdateMfaClicked}}
            />
          {{else}}
            <PrismButton
              @label={{t "mfa.enable"}}
              @onClick={{this.changeMfaModalVisibility}}
              data-test-id="button-enable-mfa"
            />
          {{/if}}

        </:content>
      </PrismCard>

    </div>

    {{#if this.isMfaModalOpen}}
      <SettingsPersonalMfaModal
        @isMfaEnforced={{this.isMfaEnforced}}
        @mfaActiveMethod={{this.mfaActiveMethod}}
        @mfaConfigs={{@mfaConfigs}}
        @onCloseModal={{this.changeMfaModalVisibility}}
        @refreshMfaConfigs={{@refreshMfaConfigs}}
        @showMfaTurnOffOrUpdateScreen={{this.showMfaTurnOffOrUpdateScreen}}
        @toggleMfaTurnOffOrUpdateScreen={{this.toggleMfaTurnOffOrUpdateScreen}}
      />
    {{/if}}
  </template>
}
